import { Star, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#FF69B4]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#87CEEB]/20 flex items-center justify-center">
              <Star className="w-4 h-4 text-[#FF69B4]" />
            </div>
            <span className="font-outfit font-bold text-lg text-[#1E293B]">Privacy Policy</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
          <h1 className="font-outfit font-bold text-2xl md:text-3xl text-[#1E293B] mb-6">Privacy Policy</h1>
          <p className="text-sm text-[#64748B] mb-6">Last updated: December 2025</p>

          <div className="space-y-6 text-[#475569]">
            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">1. Introduction</h2>
              <p>
                Welcome to FOUNDSTAR ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">2. Information We Collect</h2>
              <p className="mb-3">We collect information that you provide directly to us, including:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, password, profile picture, and user type (influencer or brand).</li>
                <li><strong>Profile Information:</strong> For influencers - niche, location, bio, social media links, follower counts. For brands - company name, logo, business details.</li>
                <li><strong>Verification Information:</strong> Social media profile links, follower counts, and engagement metrics for verification purposes.</li>
                <li><strong>Payment Information:</strong> Transaction details processed through our payment partner Razorpay. We do not store your card details.</li>
                <li><strong>Communication Data:</strong> Messages exchanged between brands and influencers on our platform.</li>
                <li><strong>Location Data:</strong> Precise location if you choose to enable auto-detection for your profile.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">3. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>To create and manage your account</li>
                <li>To facilitate connections between brands and influencers</li>
                <li>To process verification requests and payments</li>
                <li>To provide customer support</li>
                <li>To send you updates, marketing communications, and notifications</li>
                <li>To improve our platform and develop new features</li>
                <li>To detect and prevent fraud or unauthorized access</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">4. Information Sharing</h2>
              <p className="mb-3">We may share your information with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Other Users:</strong> Your profile information is visible to brands (for influencers) or influencers (for brands) to facilitate collaborations.</li>
                <li><strong>Service Providers:</strong> Third-party vendors who assist us in operating our platform (payment processors, cloud hosting, analytics).</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">5. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">6. Your Rights</h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access and receive a copy of your personal data</li>
                <li>Rectify or update your personal information</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for location tracking</li>
              </ul>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">7. Cookies</h2>
              <p>
                We use cookies and similar tracking technologies to maintain your session, remember your preferences, and analyze platform usage. You can control cookies through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">8. Children's Privacy</h2>
              <p>
                Our platform is not intended for users under 18 years of age. We do not knowingly collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">9. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="font-semibold text-lg text-[#1E293B] mb-3">10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p className="mt-2">
                <strong>Email:</strong> <a href="mailto:support@foundstar.in" className="text-[#FF69B4] hover:underline">support@foundstar.in</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
